import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowUpRight, Heart } from 'lucide-react';
import Logo from './Logo';
import { company, services } from '../utils/company';

const footerLinks = {
  company: [
    { name: 'About Us', path: '/about' },
    { name: 'Our Projects', path: '/projects' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ],
  support: [
    { name: 'Help Center', path: '/contact' },
    { name: 'Documentation', path: '/blog' },
    { name: 'Privacy Policy', path: '/about' },
    { name: 'Terms of Service', path: '/about' },
  ],
};

const Footer = () => {
  return (
    <footer className="relative bg-primary-800 text-white overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-primary-700/50 to-primary-900 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-6 bg-white rounded-xl px-4 py-3 shadow-sm">
              <Logo className="h-16 sm:h-20 w-auto min-w-[10rem]" />
            </Link>
            <p className="text-primary-100 text-sm leading-relaxed mb-6 max-w-sm">
              {company.slogan}. Leading ICT consulting and technology services in Somalia — ERP, business intelligence, networking, and digital transformation.
            </p>
            <div className="space-y-3">
              <a href={`https://maps.google.com/?q=${encodeURIComponent(company.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-primary-100 hover:text-white transition-colors text-sm">
                <MapPin size={16} className="text-primary-300 shrink-0" />
                <span>{company.address}</span>
              </a>
              <a href={`tel:${company.phoneTel}`} className="flex items-center gap-3 text-primary-100 hover:text-white transition-colors text-sm">
                <Phone size={16} className="text-primary-300 shrink-0" />
                <span>{company.phone}</span>
              </a>
              <a href={`mailto:${company.email}`} className="flex items-center gap-3 text-primary-100 hover:text-white transition-colors text-sm">
                <Mail size={16} className="text-primary-300 shrink-0" />
                <span>{company.email}</span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-2 lg:col-start-6">
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to={`/services/${service.slug}`}
                    className="text-primary-100 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {service.title}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-primary-100 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h3 className="text-white font-semibold mb-6 text-sm uppercase tracking-wider">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-primary-100 hover:text-white transition-colors text-sm flex items-center gap-1 group"
                  >
                    {link.name}
                    <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-primary-600/50 py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-primary-200 text-sm flex items-center gap-1">
            © {new Date().getFullYear()} {company.name}. Made with <Heart size={14} className="text-red-400 fill-red-400" /> in Somalia
          </p>
          <div className="flex items-center gap-6">
            <a href="https://magooltech.com" className="text-primary-200 hover:text-white transition-colors text-sm">{company.website}</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
